import type { OnInit, OnChanges, SimpleChanges } from '@angular/core';
import type { FormControl } from '@angular/forms';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type {
    CarouselOptions,
    CarouselType,
    CollageOptions,
    CollageType,
    GalleryOptions,
    GalleryWidgetData,
    GalleryTypes,
} from '@web/widgets/gallery/type/gallery-widget.type';

import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { defer, map, of, tap } from 'rxjs';
// import { wrapGrid } from 'animate-css-grid';

import { CSS_RESTRICTIONS } from '@common/constants';
import { EGalleryAttachment, EGalleryTypes } from '@common/enums';
import { WebBaseControlSettingsComponent } from '@web/widgets/common/abstract/web-base-control-settings.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { CarouselTypes, CollageTypes } from '@web/widgets/gallery/type/gallery-widget.type';

@Component({
    selector: 'web-gallery-settings',
    templateUrl: './gallery-settings.component.html',
    styleUrls: ['./gallery-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule],
})
export class GallerySettingsComponent
    extends WebBaseControlSettingsComponent<GalleryWidgetData>
    implements OnInit, OnChanges
{
    @Input() galleryType: GalleryTypes;
    @Input() isSynchronizeStyle: boolean;
    @Output() synchronizeStyleToggle = new EventEmitter<void>();
    @Output() formReady = of(this.form);
    @Output()
    valueChange = defer(() =>
        this.form.valueChanges.pipe(
            // startWith(this.form.value),
            tap((formVal) => {
                // if (!['masonry', '2col-2row'].includes(formVal?.style) && this.gridMethods === null) {
                //     setTimeout(() => {
                //         this.initGridAnimation();
                //     }, 0);
                // }
                return this.form.markAsDirty({ onlySelf: true });
            }),
            map((formValue): Partial<GalleryOptions> => formValue)
        )
    );

    BORDER_RADIUS_MIN = CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    BORDER_RADIUS_MAX = CSS_RESTRICTIONS.BORDER_RADIUS_MAX;

    protected readonly ARROW_SIZE_MIN = 16;
    protected readonly ARROW_SIZE_MAX = 64;
    protected readonly ARROW_SIZE_DEFAULT = 44;

    protected readonly PREVIEW_HEIGHT_MIN = 50;
    protected readonly PREVIEW_HEIGHT_MAX = 600;

    EGalleryTypes = EGalleryTypes;
    protected readonly MAX_IMAGE_IN_ROW = [1, 2, 3, 4, 5, 6];
    protected readonly APPEARANCE_VARIANTS = {
        carouselAppearance: CarouselTypes,
        collageAppearance: CollageTypes,
    };

    public attachmentOptions = Object.values(EGalleryAttachment);

    // gridMethods: { unwrapGrid: () => void; forceGridAnimation: () => void } | null;

    public get isMasonry(): boolean {
        if ('style' in this.form.value) {
            return ['masonry', '2col-2row'].includes(this.style.value);
        }
        return false;
    }

    public disabledRowItemsNumber(number: number): boolean {
        if (this.galleryType !== EGalleryTypes.carousel) return false;

        return this.type.value === 'thumbs-gallery' && number === 1;
    }

    public ngOnInit(): void {
        this.setFormData();

        this.cachedInitValue = this.item;
        // this.initGridAnimation();
    }

    // item оновлюється ззовні (напр. ресайз висоти thumbs на в'ю) — ре-синхронізуємо форму,
    // emitEvent: false щоб не зациклити (panel -> store -> item -> panel)
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['item'] && !changes['item'].firstChange && this.form) {
            this.form.patchValue(this.item, { emitEvent: false });
        }
    }

    // initGridAnimation() {
    //     const grid = document.querySelector(`.grid`) as HTMLElement;
    //     this.gridMethods = wrapGrid(grid, { easing: 'circOut', duration: 500 });
    // }

    // stopGridAnimation() {
    //     this.gridMethods?.unwrapGrid();
    //     this.gridMethods = null;
    // }

    protected initForm(): void {
        this.form = this.fb.record({
            imageGap: null,
            imageGapMobile: null,
            imageGapTablet: null,
            borderRadius: null,
            clickable: null,
            rowItems: null,
            rowItemsMobile: null,
            rowItemsTablet: null,
            attachment: null,
            previewAttachment: null,
            previewImageHeight: null,
            previewImageHeightMobile: null,
            previewImageHeightTablet: null,
            backgroundColor: null,
        });
    }

    protected setFormData(): void {
        if (this.galleryType === EGalleryTypes.collage) {
            this.form.removeControl('rowItems', { emitEvent: false });
            this.form.removeControl('rowItemsMobile', { emitEvent: false });
            this.form.removeControl('rowItemsTablet', { emitEvent: false });
            this.form.addControl('style', this.fb.control(null), { emitEvent: false });
        } else if (this.galleryType === EGalleryTypes.carousel) {
            this.form.addControl('knobsColor', this.fb.control(null), { emitEvent: false });
            this.form.addControl('dotsColor', this.fb.control(null), { emitEvent: false });
            this.form.addControl('autoplay', this.fb.control(null), { emitEvent: false });
            this.form.addControl('type', this.fb.control(null), { emitEvent: false });
            this.form.addControl('isShowLastPreviewEdge', this.fb.control(null), { emitEvent: false });
            this.form.addControl('arrowSize', this.fb.control(null), { emitEvent: false });
            this.form.addControl('arrowSizeMobile', this.fb.control(null), { emitEvent: false });
            this.form.addControl('arrowSizeTablet', this.fb.control(null), { emitEvent: false });
            this.form.addControl('isOutsideContainerArrow', this.fb.control(null), { emitEvent: false });

            if (!this.dotsColor.value) {
                this.dotsColor.setValue('rgba(0, 0, 0, 1)', { emitEvent: false });
            }

            //fix to old data without arrowSize value
            if (!this.arrowSize.value) {
                this.arrowSize.setValue(this.ARROW_SIZE_DEFAULT, { emitEvent: false });
            }
        }

        if (!this.attachment.value) {
            this.attachment.setValue(EGalleryAttachment.cover, { emitEvent: false });
        }

        if (!this.previewAttachment.value) {
            this.previewAttachment.setValue(EGalleryAttachment.cover, { emitEvent: false });
        }

        if (!this.backgroundColor.value) {
            this.backgroundColor.setValue('rgba(0, 0, 0, 0)', { emitEvent: false });
        }

        this.form.patchValue(this.item, { emitEvent: false });

        //fix to old data without mobile and tablet values
        if (this.galleryType !== EGalleryTypes.collage) {
            if (!this.rowItemsMobile.value) {
                this.rowItemsMobile.setValue(this.rowItems.value, { emitEvent: false });
                this.rowItemsTablet.setValue(this.rowItems.value, { emitEvent: false });
            }
        }

        if (!this.imageGapMobile.value) {
            this.imageGapMobile.setValue(this.imageGap.value, { emitEvent: false });
            this.imageGapTablet.setValue(this.imageGap.value, { emitEvent: false });
        }

        //fix to old data without arrowSize mobile and tablet values
        if (this.galleryType === EGalleryTypes.carousel && !this.arrowSizeMobile.value) {
            this.arrowSizeMobile.setValue(this.arrowSize.value, { emitEvent: false });
            this.arrowSizeTablet.setValue(this.arrowSize.value, { emitEvent: false });
        }

        //fix to old data without isOutsideContainerArrow value
        if (this.galleryType === EGalleryTypes.carousel && this.isOutsideContainerArrow.value == null) {
            this.isOutsideContainerArrow.setValue(false, { emitEvent: false });
        }

        //fix to old data without previewImageHeight mobile and tablet values
        if (this.galleryType === EGalleryTypes.carousel && !this.previewImageHeightMobile.value) {
            this.previewImageHeightMobile.setValue(this.previewImageHeight.value, { emitEvent: false });
            this.previewImageHeightTablet.setValue(this.previewImageHeight.value, { emitEvent: false });
        }
    }

    public get attachment(): FormControl<GalleryOptions['attachment']> {
        return this.form.get('attachment') as FormControl<GalleryOptions['attachment']>;
    }

    public get previewAttachment(): FormControl<CarouselOptions['previewAttachment']> {
        return this.form.get('previewAttachment') as FormControl<CarouselOptions['previewAttachment']>;
    }

    // grid and carousel control
    public get rowItems(): FormControl<number> {
        return this.form.get('rowItems') as FormControl<number>;
    }

    public get rowItemsMobile(): FormControl<number> {
        return this.form.get('rowItemsMobile') as FormControl<number>;
    }

    public get rowItemsTablet(): FormControl<number> {
        return this.form.get('rowItemsTablet') as FormControl<number>;
    }

    // shared controls
    public get clickable(): FormControl<GalleryOptions['clickable']> {
        return this.form.get('clickable') as FormControl<GalleryOptions['clickable']>;
    }

    public get borderRadius(): FormControl<GalleryOptions['borderRadius']> {
        return this.form.get('borderRadius') as FormControl<GalleryOptions['borderRadius']>;
    }

    public get imageGap(): FormControl<GalleryOptions['imageGap']> {
        return this.form.get('imageGap') as FormControl<GalleryOptions['imageGap']>;
    }

    public get imageGapMobile(): FormControl<GalleryOptions['imageGapMobile']> {
        return this.form.get('imageGapMobile') as FormControl<GalleryOptions['imageGapMobile']>;
    }

    public get imageGapTablet(): FormControl<GalleryOptions['imageGapTablet']> {
        return this.form.get('imageGapTablet') as FormControl<GalleryOptions['imageGapTablet']>;
    }

    // carousel type
    public get type(): FormControl<CarouselType> {
        return this.form.get('type') as FormControl<CarouselType>;
    }

    public get autoplay(): FormControl<CarouselOptions['autoplay']> {
        return this.form.get('autoplay') as FormControl<CarouselOptions['autoplay']>;
    }

    public get knobsColor(): FormControl<CarouselOptions['knobsColor']> {
        return this.form.get('knobsColor') as FormControl<CarouselOptions['knobsColor']>;
    }

    public get dotsColor(): FormControl<CarouselOptions['dotsColor']> {
        return this.form.get('dotsColor') as FormControl<CarouselOptions['dotsColor']>;
    }

    public get backgroundColor(): FormControl<CarouselOptions['backgroundColor']> {
        return this.form.get('backgroundColor') as FormControl<CarouselOptions['backgroundColor']>;
    }

    public get isShowLastPreviewEdge(): FormControl<CarouselOptions['isShowLastPreviewEdge']> {
        return this.form.get('isShowLastPreviewEdge') as FormControl<CarouselOptions['isShowLastPreviewEdge']>;
    }

    public get previewImageHeight(): FormControl<CarouselOptions['previewImageHeight']> {
        return this.form.get('previewImageHeight') as FormControl<CarouselOptions['previewImageHeight']>;
    }

    public get previewImageHeightMobile(): FormControl<CarouselOptions['previewImageHeightMobile']> {
        return this.form.get('previewImageHeightMobile') as FormControl<CarouselOptions['previewImageHeightMobile']>;
    }

    public get previewImageHeightTablet(): FormControl<CarouselOptions['previewImageHeightTablet']> {
        return this.form.get('previewImageHeightTablet') as FormControl<CarouselOptions['previewImageHeightTablet']>;
    }

    public get arrowSize(): FormControl<CarouselOptions['arrowSize']> {
        return this.form.get('arrowSize') as FormControl<CarouselOptions['arrowSize']>;
    }

    public get arrowSizeMobile(): FormControl<CarouselOptions['arrowSizeMobile']> {
        return this.form.get('arrowSizeMobile') as FormControl<CarouselOptions['arrowSizeMobile']>;
    }

    public get arrowSizeTablet(): FormControl<CarouselOptions['arrowSizeTablet']> {
        return this.form.get('arrowSizeTablet') as FormControl<CarouselOptions['arrowSizeTablet']>;
    }

    public get isOutsideContainerArrow(): FormControl<CarouselOptions['isOutsideContainerArrow']> {
        return this.form.get('isOutsideContainerArrow') as FormControl<CarouselOptions['isOutsideContainerArrow']>;
    }

    // collage style
    public get style(): FormControl<CollageOptions['style']> {
        return this.form.get('style') as FormControl<CollageOptions['style']>;
    }

    //setters
    public setRowItems(value: number): void {
        if (value === this.rowItems.value) return;
        this.rowItems.patchValue(value);

        if (this.isSynchronizeStyle) {
            this.setRowItemMobile(value);
            this.setRowItemsTablet(value);
        }
    }

    public setRowItemMobile(value: number): void {
        if (value === this.rowItemsMobile.value) return;
        this.rowItemsMobile.patchValue(value);
    }

    public setRowItemsTablet(value: number): void {
        if (value === this.rowItemsTablet.value) return;
        this.rowItemsTablet.patchValue(value);
    }

    public setType(value: CarouselType): void {
        if (value === this.type.value) return;
        if (this.rowItems.value === 1 && value === 'thumbs-gallery') {
            this.rowItems.patchValue(2, { emitEvent: false });
        }
        this.type.patchValue(value);
    }

    public setKnobsColor({ value }: ColorPickerEventPayload): void {
        if (value === this.knobsColor.value) return;
        this.knobsColor.patchValue(value);
    }

    public setDotsColor({ value }: ColorPickerEventPayload): void {
        if (value === this.dotsColor.value) return;
        this.dotsColor.patchValue(value);
    }

    public setBackgroundColor({ value }: ColorPickerEventPayload): void {
        if (value === this.backgroundColor.value) return;
        this.backgroundColor.patchValue(value);
    }

    public setCollageStyle(value: CollageType): void {
        if (value === this.style.value) return;

        // if (value === 'masonry' || value === '2col-2row') {
        //     this.stopGridAnimation();
        // }
        this.style.patchValue(value);
    }

    public setImageGap(value: number): void {
        // this.stopGridAnimation();
        this.imageGap.patchValue(value);

        if (this.isSynchronizeStyle) {
            this.setImageGapMobile(value);
            this.setImageGapTablet(value);
        }
    }

    public setImageGapMobile(value: number): void {
        this.imageGapMobile.patchValue(value);
    }

    public setImageGapTablet(value: number): void {
        this.imageGapTablet.patchValue(value);
    }

    public setBorderRadius(value: number): void {
        this.borderRadius.patchValue(value);
    }

    public setArrowSize(value: number): void {
        this.arrowSize.patchValue(value);

        if (this.isSynchronizeStyle) {
            this.setArrowSizeMobile(value);
            this.setArrowSizeTablet(value);
        }
    }

    public setArrowSizeMobile(value: number): void {
        this.arrowSizeMobile.patchValue(value);
    }

    public setArrowSizeTablet(value: number): void {
        this.arrowSizeTablet.patchValue(value);
    }

    public setPreviewImageHeight(value: number): void {
        this.previewImageHeight.patchValue(value);

        if (this.isSynchronizeStyle) {
            this.setPreviewImageHeightMobile(value);
            this.setPreviewImageHeightTablet(value);
        }
    }

    public setPreviewImageHeightMobile(value: number): void {
        this.previewImageHeightMobile.patchValue(value);
    }

    public setPreviewImageHeightTablet(value: number): void {
        this.previewImageHeightTablet.patchValue(value);
    }

    public onSynchronizeStyleToggle(): void {
        if (!this.isSynchronizeStyle) {
            this.setRowItemMobile(this.rowItems.value);
            this.setRowItemsTablet(this.rowItems.value);
            this.setImageGapMobile(this.imageGap.value);
            this.setImageGapTablet(this.imageGap.value);
            this.setArrowSizeMobile(this.arrowSize.value);
            this.setArrowSizeTablet(this.arrowSize.value);
            this.setPreviewImageHeightMobile(this.previewImageHeight.value);
            this.setPreviewImageHeightTablet(this.previewImageHeight.value);
        }

        this.synchronizeStyleToggle.emit();
    }
}
